import * as React from 'react';
import { Dispatch, ReactElement } from 'react';

import { pluralize } from '../../utils';
import Panel from '../common/Panel';

import { IAction, IState } from './reducer';
import BuilderSearch, {
    BuilderSearchItemRenderer,
    IProps as IBuilderSearchProps,
} from './search/BuilderSearch';
import BuilderSearchItem from './search/BuilderSearchItem';

export interface IProps {
    dispatch: Dispatch<IAction>;
    searchImplementations: IBuilderSearchProps['implementations'];
    state: IState;
}

export default function BuilderManager({
    dispatch,
    searchImplementations,
    state,
}: IProps): ReactElement {
    const renderItem: BuilderSearchItemRenderer = (
        item,
        model,
    ): ReactElement => {
        // Legacy search doesn't return online and correction_type so we need to check more strictly
        const alreadyAdded = state.items.some(
            (i) => i.item.uri === item.uri,
        );
        const isOffline = item.online === false;
        const hasNoCorrection =
            item.correction_type !== undefined &&
            item.correction_type === 'None';

        const canBeAdded = !alreadyAdded && !isOffline && !hasNoCorrection;

        let notAddableReason = 'This project cannot be added';
        if (alreadyAdded) {
            notAddableReason = 'This project is already in this curriculum';
        } else if (isOffline) {
            notAddableReason = 'This project is offline';
        } else if (hasNoCorrection) {
            notAddableReason =
                'This project has no correction (no online checks of any kind)';
        }

        return (
            <BuilderSearchItem
                canBeAdded={canBeAdded}
                dispatch={dispatch}
                item={item}
                key={`${model.name}_${item.id}`}
                model={model}
                notAddableReason={notAddableReason}
                state={state}
            />
        );
    };

    return (
        <Panel icon="wrench" title="Manage">
            <div className="list-group">
                <div className="list-group-item">
                    <strong className="fs-3">
                        {pluralize(state.workHours, 'hour')}
                    </strong>
                    <div className="text-muted">
                        {pluralize(state.items.length, 'project')} in{' '}
                        {pluralize(state.blocks.length, 'block')}
                    </div>
                </div>

                {state.canEdit && (
                    <BuilderSearch
                        implementations={searchImplementations}
                        renderItem={renderItem}
                    />
                )}
            </div>
        </Panel>
    );
}
